import type { V3Event, V3EventType } from './types';

const KNOWN: ReadonlySet<V3EventType> = new Set([
  'message.created', 'card.updated', 'flood.updated', 'trip.updated', 'resync', 'ping',
]);

/**
 * Đổi một tin SSE thô (event/data/id) thành V3Event có kiểu.
 * Sự kiện lạ hoặc data hỏng ⇒ null (bỏ qua, không làm sập luồng).
 */
export function parseV3Event(m: { event: string; data: string; id?: string }): V3Event | null {
  if (!KNOWN.has(m.event as V3EventType)) return null;
  let data: unknown;
  try {
    data = m.data ? JSON.parse(m.data) : {};
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const ok =
    (m.event === 'message.created' && typeof d.message === 'object' && d.message !== null) ||
    (m.event === 'card.updated' && typeof d.card === 'object' && d.card !== null) ||
    (m.event === 'flood.updated' && typeof d.flood === 'object' && d.flood !== null) ||
    (m.event === 'trip.updated' && typeof d.trip === 'object' && d.trip !== null) ||
    m.event === 'resync' || m.event === 'ping';
  if (!ok) return null;
  return { type: m.event, id: m.id, data } as V3Event;
}

type HandlerMap = { [K in V3EventType]?: (data: Extract<V3Event, { type: K }>['data']) => void };

/**
 * Tạo hàm onMessage cho luồng SSE: phân phối theo loại sự kiện.
 * `resync` ⇒ gọi onResync để màn đang mở tải lại toàn bộ dữ liệu (05 §2 quy tắc 4).
 */
export function createV3EventRouter(handlers: HandlerMap & { onResync?: (reason: string) => void }) {
  return (m: { event: string; data: string; id?: string }): void => {
    const e = parseV3Event(m);
    if (!e) return;
    if (e.type === 'resync') handlers.onResync?.(e.data.reason);
    const h = handlers[e.type] as ((d: unknown) => void) | undefined;
    h?.(e.data);
  };
}

/** Trạng thái kết nối hiển thị cho người dùng (05 FE-1.1): connected → reconnecting → offline. */
export type V3ConnectionStatus = 'connected' | 'reconnecting' | 'offline';

/** Backoff v3: 1 s, 2 s, 5 s, rồi tối đa 10 s (05 FE-1.1). */
export const V3_BACKOFF_STEPS_MS = [1000, 2000, 5000, 10_000] as const;
export const v3BackoffDelay = (attempt: number): number =>
  V3_BACKOFF_STEPS_MS[Math.min(attempt, V3_BACKOFF_STEPS_MS.length - 1)]!;

/** Quá 45 s không nhận gì (ping 15 s/lần) ⇒ coi như mất kết nối (10 §4). */
export const V3_IDLE_TIMEOUT_MS = 45_000;
